import React from 'react';
import posthog, { type PostHogConfig } from 'posthog-js';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import { PostHogBootstrap } from './types';
import buildFlagStyles from './build-flag-styles';
import { FLAG_STYLE_TAG_CLASSNAME } from './constants';
import PROXY_PATHS from '../../endpoints/netlify-edge/proxy/proxy-paths';
import { POSTHOG } from '../../constants';

const apiKey = POSTHOG.CLIENT_SIDE.POSTHOG_PROJECT_API_KEY;

type FlagRecord = Record<string, string | 'control' | 'variant'>;

interface AvailableFlags {
    flags: FlagRecord;
    originals: FlagRecord;
    overrides: FlagRecord;
    booleanFlags: string[];
    setOverride: (
        flagName: string,
        variant: string | 'control' | 'variant' | null
    ) => Promise<void>;
}

const AvailableFlagsContext = React.createContext(null as unknown as AvailableFlags);

export const getPhBootstrap = (): PostHogBootstrap | null => {
    if (typeof window === 'undefined') return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-underscore-dangle
    return (window as any).__POSTHOG_BOOTSTRAP__ || null;
};

interface FlagState {
    flags: FlagRecord;
    originals: FlagRecord;
    overrides: FlagRecord;
    booleanFlags: string[];
}

const EventsProvider = ({ children }: React.PropsWithChildren<object>): JSX.Element => {
    const [flagState, setFlagState] = React.useState<FlagState>({
        flags: {},
        originals: {},
        overrides: {},
        booleanFlags: [],
    });

    React.useEffect(() => {
        if (!apiKey) return undefined;

        const bootstrap = getPhBootstrap();

        // No bootstrap → don't initialize PostHog at all. This keeps a single
        // distinct_id across the inline edge-function script → PostHog → Invoca
        // chain. Covers local dev (gatsby serve/develop) and any edge-function
        // failure. The server-side /ingest fallback still captures Invoca events
        // on the invoca_id so nothing is lost.
        if (!bootstrap) return undefined;

        // Skips still get flag state hydrated from bootstrap, but no PostHog init / tracking.
        if (
            // eslint-disable-next-line no-underscore-dangle
            bootstrap._debug?.actionMethod === 'skipped-bot'
            // eslint-disable-next-line no-underscore-dangle
          || bootstrap._debug?.actionMethod === 'skipped-dev'
            // eslint-disable-next-line no-underscore-dangle
          || bootstrap._debug?.actionMethod === 'skipped-default'
        ) {
            setFlagState({
                flags: bootstrap.featureFlags || {},
                originals: bootstrap.featureMeta?.original || {},
                overrides: bootstrap.featureMeta?.override || {},
                booleanFlags: bootstrap.booleanFlags || [],
            });
            return undefined;
        }

        const isProduction = POSTHOG.CLIENT_SIDE.SHOULD_RUN_POSTHOG === 'true';

        if (!isProduction) return undefined;

        const options: Partial<PostHogConfig> = {
            api_host: `${window.location.origin}${PROXY_PATHS.ph}`,
            defaults: '2025-05-24',
            autocapture: isProduction,
            capture_pageview: isProduction,
            capture_pageleave: isProduction,
            disable_session_recording: !isProduction,
            advanced_disable_feature_flags: !isProduction,
            opt_out_capturing_by_default: !isProduction,
            bootstrap,
        };

        posthog.init(apiKey, options);

        // Set window.posthog_session_id for Invoca immediately, then keep it updated on session rotation
        const currentSessionId = (posthog as { get_session_id?: () => string }).get_session_id?.();
        if (currentSessionId) {
            window.posthog_session_id = currentSessionId;
        }
        posthog.onSessionId((sessionId) => {
            window.posthog_session_id = sessionId;
        });

        // NOTE: window.posthog_distinct_id is set synchronously by the inline script
        // in process-html-with-flags.ts from the same distinct_id carried by bootstrap.
        // We deliberately don't re-set it here so the inline script remains the single
        // source of truth and there's no window between init and this line where the
        // two could disagree for Invoca.

        // Attach Invoca ID as a person property for cross-system querying.
        // Invoca's JS loads asynchronously via GTM so the ID may not be available
        // immediately — check the cookie first (available for returning users before
        // any JS runs), then fall back to localStorage. Retry with increasing delays.
        const getInvocaId = (): string | null => {
            try {
                // Cookie is available immediately for returning users
                const match = document.cookie
                    .split(';')
                    .find(c => c.trim().startsWith('invoca_session='));
                if (match) {
                    const raw = decodeURIComponent(match.split('=').slice(1).join('=').trim());
                    const parsed = JSON.parse(raw);
                    const id = parsed?.session?.invoca_id;
                    if (typeof id === 'string' && id) return id;
                }
            } catch { /* ignore cookie parse errors */ }
            try {
                return localStorage.getItem('invoca_id');
            } catch { /* ignore localStorage errors */ }
            return null;
        };

        const invocaRetryDelays = [0, 2000, 5000, 10000, 20000];
        let invocaTimeoutId: ReturnType<typeof setTimeout> | null = null;

        // Local-storage key tracking the last invoca_id we synced to PostHog for
        // this browser. Lets us detect when Invoca has rotated the ID for a
        // returning visitor and emit a visibility event before silently
        // overwriting the person property.
        const LAST_SYNCED_INVOCA_ID_KEY = 'sp_last_synced_invoca_id';

        const trySetInvocaId = (attemptIndex: number): void => {
            const invocaId = getInvocaId();
            if (invocaId) {
                let lastSynced: string | null = null;
                try {
                    lastSynced = localStorage.getItem(LAST_SYNCED_INVOCA_ID_KEY);
                } catch { /* ignore localStorage errors */ }

                if (lastSynced && lastSynced !== invocaId) {
                    posthog.capture('invoca_id_changed', {
                        from: lastSynced,
                        to: invocaId,
                    });
                }

                posthog.setPersonProperties({ invoca_id: invocaId });

                try {
                    localStorage.setItem(LAST_SYNCED_INVOCA_ID_KEY, invocaId);
                } catch { /* ignore localStorage errors */ }
                return;
            }
            if (attemptIndex + 1 < invocaRetryDelays.length) {
                invocaTimeoutId = setTimeout(
                    () => trySetInvocaId(attemptIndex + 1),
                    invocaRetryDelays[attemptIndex + 1],
                );
            }
        };

        trySetInvocaId(0);

        // Push PostHog distinct_id to GTM dataLayer for GA cross-referencing
        if (Array.isArray(window.dataLayer)) {
            window.dataLayer.push({
                event: 'SiteVisitorId',
                SiteVisitorId: posthog.get_distinct_id(),
            });
        }

        // Single setState call to avoid multiple re-renders
        setFlagState({
            flags: bootstrap.featureFlags || {},
            originals: bootstrap.featureMeta?.original || {},
            overrides: bootstrap.featureMeta?.override || {},
            booleanFlags: bootstrap.booleanFlags || [],
        });

        return () => {
            if (invocaTimeoutId !== null) clearTimeout(invocaTimeoutId);
        };
    }, []);

    const setOverride = React.useCallback(
        async (flagName: string, variant: string | 'control' | 'variant' | null) => {
            const url = new URL('/api/ph/set-flag', window.location.origin);
            url.searchParams.set('flag', flagName);
            if (variant) url.searchParams.set('variant', variant);

            const overrideUpdates: FlagRecord = {};
            const combinedUpdates: FlagRecord = {};

            Object.keys(flagState.originals).forEach((name) => {
                if (name === flagName && !variant) {
                    combinedUpdates[name] = flagState.originals[name];
                    return;
                }

                if (name === flagName && variant) {
                    combinedUpdates[name] = variant;
                    overrideUpdates[name] = variant;
                    return;
                }

                if (flagState.overrides[name]) {
                    combinedUpdates[name] = flagState.overrides[name];
                    overrideUpdates[name] = flagState.overrides[name];
                    return;
                }

                combinedUpdates[name] = flagState.originals[name];
            });

            const inlineStylesArray = buildFlagStyles(combinedUpdates);
            const existingStyleEl = document.getElementById(
                FLAG_STYLE_TAG_CLASSNAME,
            ) as HTMLStyleElement | null;

            if (existingStyleEl) {
                existingStyleEl.innerHTML = inlineStylesArray.join('\n');
            }

            setFlagState(prev => ({ ...prev, flags: combinedUpdates, overrides: overrideUpdates }));
            await fetch(url.toString(), { method: 'POST' });
        },
        [flagState.originals, flagState.overrides],
    );

    const value = React.useMemo(
        () => ({
            flags: flagState.flags,
            originals: flagState.originals,
            overrides: flagState.overrides,
            booleanFlags: flagState.booleanFlags,
            setOverride,
        }),
        [flagState, setOverride],
    );

    return (
        <PostHogProvider client={posthog}>
            <AvailableFlagsContext.Provider value={value}>
                {children}
            </AvailableFlagsContext.Provider>
        </PostHogProvider>
    );
};

export const useAvailableFlags = (): AvailableFlags => React.useContext(AvailableFlagsContext);

type CaptureGoal = (eventName: string, eventData: Record<string, string>) => void;

export const useGoal = (): CaptureGoal => {
    const posthog = usePostHog();

    return React.useCallback((eventName, eventData) => {
        posthog?.capture(eventName, eventData);
    }, [posthog]);
};

export default EventsProvider;
